import { Container, GridItem } from 'components/Layout';
import { Heading, Text } from 'components/Typography';
import styled from '@emotion/styled';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useDataAccessProfiles } from 'pages/hooks';
import { DataGrid, getSortObject } from 'components/DataGrid';
import { dataAccessProfilesTableHeaders } from './type';
import { useCompaniesOptions, useDataAccessProfilesParams } from './hooks';
import { SelectOption, SortObject } from 'components/types';
import { useEffect, useState } from 'react';
import { createAdminDataAccessProfilesUrl, ROUTES } from 'core/routes';
import {
  DataAccessProfile,
  SortOrder,
} from 'api/resources/models/AutoGenerated';
import { orderBy } from 'lodash';
import { disabledCompanySearchColumns } from 'config';
import {
  MainPageContainer,
  UpperInfoContainer,
  PageHeader,
  TableContainer,
} from 'components/PageLayout';
import InfoText from 'components/InfoText';
import InputSelect from 'components/InputSelect';
import { TextField } from 'components/TextField';
import { NO_PROFILES_DATA } from './constants';
import { BackButtonItem } from 'components/ReportsComponents/BackButton';
import { BACK_TO_ADMINISTRATION } from 'pages/constants';
import { DesktopButton, MobileButton } from './CreateButtons';

export function DataAccessProfiles() {
  const location = useLocation();
  const companiesOptions = useCompaniesOptions();
  const navigate = useNavigate();
  const urlParams = useDataAccessProfilesParams(location.search);
  const [sortObject, setSortObject] = useState<SortObject>({
    sortString: urlParams.sortString ?? '',
    sortOrder: urlParams.sortOrder ?? undefined,
  });

  const [selectedCompanyId, setSelectedCompanyId] = useState(
    urlParams.companyId
  );

  const { dataAccessProfiles, isDataAccessProfilesLoading } =
    useDataAccessProfiles({
      companyId: urlParams.companyId ?? undefined,
      enabled: true,
    });

  const [sortedDataAccessProfiles, setSortedDataAccessProfiles] =
    useState(dataAccessProfiles);

  const disabledSortColumns =
    (selectedCompanyId && disabledCompanySearchColumns) || undefined;

  useEffect(() => {
    setSortedDataAccessProfiles(sortProfiles(sortObject));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAccessProfiles]);

  useEffect(() => {
    navigateToAccessDataProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortObject, selectedCompanyId]);

  return (
    <MainPageContainer
      isLoading={isDataAccessProfilesLoading}
      direction="column"
      wrap="nowrap"
    >
      <UpperInfoContainer direction="column">
        <BackButtonItem text={BACK_TO_ADMINISTRATION}/>
        <PageHeader>
          <Heading>Data Access Profiles</Heading>
        </PageHeader>
        <TextItem>
          <Text fontSize="m">Select Company</Text>
        </TextItem>
        <Container direction="row">
          <CompanyContainer md={3} sm={12} xs={12}>
            <InputSelect
              options={companiesOptions}
              value={
                companiesOptions?.find((e) => e.id == selectedCompanyId) ??
                undefined
              }
              onChange={(e: SelectOption | null) =>
                handleCompanyIdChange(e?.id)
              }
              renderInput={(params) => <TextField {...params} />}
              forcePopupIcon
            />
          </CompanyContainer>
          <DesktopButton/>
        </Container>
      </UpperInfoContainer>
      {!isDataAccessProfilesLoading &&
        (!sortedDataAccessProfiles ||
          sortedDataAccessProfiles.length === 0) && (
          <InfoText>{NO_PROFILES_DATA}</InfoText>
        )}
      <TableContainer direction="column">
        <DataGrid
          isLoading={isDataAccessProfilesLoading}
          disabledSortColumns={disabledSortColumns}
          headers={dataAccessProfilesTableHeaders}
          data={sortedDataAccessProfiles}
          onRowClick={rowClick}
          onSort={sort}
          sortObject={sortObject}
        />
      </TableContainer>
      <MobileButton/>
    </MainPageContainer>
  );

  function rowClick(row: DataAccessProfile) {
    const route = generatePath(ROUTES.dataAccessProfileEdit, {
      id: row.id,
    });

    navigate(route)
  }

  function sort(sortValue: keyof DataAccessProfile) {
    setSortObject((prevValue) => {
      const sortObj = getSortObject(sortValue, prevValue);
      setSortedDataAccessProfiles(sortProfiles(sortObj));
      return sortObj;
    });
  }

  function sortProfiles(sortObj: SortObject) {
    return orderBy(
      dataAccessProfiles,
      sortObj.sortString,
      sortObj.sortOrder === SortOrder.Descending ? 'desc' : 'asc'
    );
  }

  function navigateToAccessDataProfiles() {
    navigate(
      createAdminDataAccessProfilesUrl(
        selectedCompanyId ?? undefined,
        sortObject.sortString ?? undefined,
        sortObject.sortOrder
      ), 
      { replace: true }
    );
  }

  function handleCompanyIdChange(companyId?: number) {
    if (companyId) setSelectedCompanyId(companyId);
    setSortObject({});
  }
}

const TextItem = styled(GridItem)`
  padding-bottom: 12px;
`;

const CompanyContainer = styled(GridItem)`
  @media (max-width: ${({ theme }) => `${theme.breakpoints.values.sm}px`}) {
    margin-bottom: 6px;
  }
`;
